import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { HubAvatar } from "@/components/social/HubAvatar";
import { useHubPublicProfile } from "@/hooks/hub/useHubPublicProfile";
import { hubBadgeReplacesRole, hubBadgeStyle } from "@/lib/hub/hubBadges";
import { hubMemberSinceLabel, hubTierForPosts } from "@/lib/hub/hubTiers";
import { cn } from "@/lib/utils";

/**
 * LR HUB quick profile popup — READ-ONLY.
 *
 * Opens when a member's avatar is clicked anywhere in the Hub feed (posts and
 * comments). Shows only that member's Hub identity + Hub activity stats. There
 * are deliberately NO actions (no connect / DM / follow) and no links out.
 *
 * Hub-isolated: data comes from `useHubPublicProfile` (hub_settings +
 * hub_posts/hub_post_comments counts) only.
 */

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="flex-1 rounded-lg bg-muted/50 px-2 py-2 text-center">
      <div className="text-sm font-bold text-foreground">{value}</div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

export function HubProfilePopup({
  userId,
  open,
  onOpenChange,
}: {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: profile, isLoading } = useHubPublicProfile(userId, open);

  const badge = hubBadgeStyle(profile?.hub_badge);
  const tier = hubTierForPosts(profile?.posts_count ?? 0);
  const showRole = !!profile?.role && !hubBadgeReplacesRole(profile?.hub_badge);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[320px] p-0">
        <div className="flex flex-col items-center px-5 pb-5 pt-6 text-center">
          {isLoading || !profile ? (
            <>
              <div className="h-24 w-24 animate-pulse rounded-full bg-muted" />
              <div className="mt-3 h-4 w-32 animate-pulse rounded bg-muted" />
              <DialogTitle className="sr-only">Hub member</DialogTitle>
              <DialogDescription className="sr-only">Loading Hub profile</DialogDescription>
            </>
          ) : (
            <>
              <HubAvatar
                username={profile.display_name}
                avatarPath={profile.avatar_path}
                size="xl"
              />

              <DialogTitle className="mt-3 text-base font-bold text-foreground">
                {profile.display_name}
              </DialogTitle>
              <DialogDescription className="text-[12px] text-muted-foreground">
                @{profile.handle}
              </DialogDescription>

              <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
                {badge ? (
                  <span
                    title={badge.title}
                    className={cn(
                      "rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide",
                      badge.className,
                    )}
                  >
                    {badge.label}
                  </span>
                ) : null}
                <span
                  title={tier.title}
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide",
                    tier.className,
                  )}
                >
                  {tier.label}
                </span>
                {showRole ? (
                  <span className="rounded bg-brand-teal-subtle px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-brand-teal-deep">
                    {profile.role}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 flex w-full gap-2">
                <Stat value={profile.posts_count} label="Posts" />
                <Stat value={profile.comments_count} label="Comments" />
                <Stat value={hubMemberSinceLabel(profile.member_since)} label="Member Since" />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default HubProfilePopup;
